import React, { createContext, useContext, useEffect, useRef } from "react";
import { Heartbeep } from "./index";
import type { HeartbeepConfig, EventType, TrackEventPayload } from "./types";

interface HeartbeepContextValue {
  heartbeep: Heartbeep | null;
  track: (event: EventType, payload?: TrackEventPayload) => void;
  pageview: (name?: string) => void;
  click: (name: string, metadata?: Record<string, any>) => void;
  purchase: (name: string, metadata?: Record<string, any>) => void;
}

const HeartbeepContext = createContext<HeartbeepContextValue | null>(null);

export interface HeartbeepProviderProps {
  children: React.ReactNode;
  siteId: string;
  apiUrl?: string;
  debug?: boolean;
}

export function HeartbeepProvider({
  children,
  siteId,
  apiUrl,
  debug,
}: HeartbeepProviderProps) {
  const heartbeepRef = useRef<Heartbeep | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && !heartbeepRef.current) {
      const config: HeartbeepConfig = {
        siteId,
        apiUrl,
        debug,
      };
      heartbeepRef.current = new Heartbeep(config);
    }
  }, [siteId, apiUrl, debug]);

  const contextValue: HeartbeepContextValue = {
    heartbeep: heartbeepRef.current,
    track: (event, payload) => {
      heartbeepRef.current?.track(event, payload);
    },
    pageview: name => {
      heartbeepRef.current?.pageview(name);
    },
    click: (name, metadata) => {
      heartbeepRef.current?.click(name, metadata);
    },
    purchase: (name, metadata) => {
      heartbeepRef.current?.purchase(name, metadata);
    },
  };

  return (
    <HeartbeepContext.Provider value={contextValue}>
      {children}
    </HeartbeepContext.Provider>
  );
}

export function useHeartbeep() {
  const context = useContext(HeartbeepContext);
  if (!context) {
    throw new Error("useHeartbeep must be used within a HeartbeepProvider");
  }
  return context;
}

// Auto-track page views on route change
export function AutoPageview({ children }: { children?: React.ReactNode }) {
  const { pageview } = useHeartbeep();

  useEffect(() => {
    pageview();

    // Listen for route changes (works with Next.js and React Router)
    const handleRouteChange = () => pageview();

    window.addEventListener("popstate", handleRouteChange);
    return () => window.removeEventListener("popstate", handleRouteChange);
  }, [pageview]);

  return <>{children}</>;
}
